<template>
  <v-dialog
    :model-value="isVisible"
    width="600"
    persistent
    @click:outside="closeDialog"
    data-testid="edit-loadtype-trigger-dialog">
    <v-card>
      <v-form>
        <v-card-title class="d-flex flex-row ma-1 mb-0">
          <h4 class="typography-heading-md">Edit Form</h4>
          <v-spacer></v-spacer>
          <v-btn icon="mdi-close" variant="text" density="comfortable" @click="closeDialog"></v-btn>
        </v-card-title>
        <v-card-text>
          <div class="d-flex flex-column ma-2 gap-4">
            <div>
              <label>
                <strong>
                  Form name
                  <span class="text-red">*</span>
                </strong>
              </label>
              <v-text-field
                v-model="customFormNameRef"
                class="mt-3"
                required
                placeholder="e.g.: Refrigerated Cargo Only"
                variant="outlined"
                data-testid="edit-loadtype-trigger-name-input" />
            </div>

            <div>
              <label>
                <strong>
                  Load Types
                  <span class="text-red">*</span>
                </strong>
              </label>
              <load-type-select
                :selected-warehouse="warehouse"
                placeholder="Select at least one"
                variant="outlined"
                outlined
                :visible-count="3"
                :return-object="false"
                v-model="loadTypesRef"
                :clearable="false"
                hide-icon
                :show-org-load-types="false"
                required
                multi-select />
            </div>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="ma-2">
          <v-spacer></v-spacer>
          <v-btn
            variant="outlined"
            @click="closeDialog"
            data-testid="edit-loadtype-trigger-cancel-button">
            Nevermind
          </v-btn>
          <v-btn
            data-testid="edit-loadtype-trigger-submit-button"
            :disabled="!isFormValid"
            class="bg-primary"
            @click="submit">
            Save Changes
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { computed, defineComponent, ref, onBeforeMount } from 'vue';
import { useNotify } from '@/composables';
import { first } from 'lodash';
import warehouseService from '@satellite/services/warehouse-service';

export default defineComponent({
  name: 'CustomFormsEditLoadTypeTriggerDialog',
  emits: ['close', 'form-updated'],
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    warehouse: {
      type: Object,
      required: true
    },
    triggers: {
      type: Array,
      required: true
    }
  },
  setup(props, { emit }) {
    const notify = useNotify();

    const parentCode = ref(null);
    const customFormNameRef = ref('');
    const loadTypesRef = ref([]);

    const isFormValid = computed(
      () => loadTypesRef.value.length > 0 && customFormNameRef.value.trim().length > 0
    );

    const closeDialog = () => {
      emit('close');
    };

    const submit = () => {
      if (!isFormValid.value) {
        return;
      }
      return warehouseService
        .updateLoadTypesCustomForms(
          props.warehouse.id,
          parentCode.value,
          loadTypesRef.value,
          customFormNameRef.value
        )
        .then(res => {
          if (res?.ok) {
            notify('Load Type Form updated successfully', 'success');
            emit('form-updated', res.trigger);
          } else {
            throw new Error('Failed to update Load Type Form');
          }
        })
        .catch(err => {
          console.error(err);
          notify('Failed to update Load Type Form', 'error');
        })
        .finally(() => {
          closeDialog();
        });
    };

    onBeforeMount(() => {
      // In this case, all the triggers are related by the parent code, as they have the same name
      // the only thing that differs is the object id (load type)
      const firstTrigger = first(props.triggers);

      if (!firstTrigger || !firstTrigger.flow) {
        console.warn('Invalid trigger data', props.triggers);
        return;
      }

      customFormNameRef.value = firstTrigger.flow.formFrom.name || '';
      parentCode.value = firstTrigger.flow.parentCode;

      loadTypesRef.value = [...new Set(props.triggers.map(trigger => trigger.objectId))];
    });

    return {
      loadTypesRef,
      customFormNameRef,
      closeDialog,
      isFormValid,
      submit
    };
  }
});
</script>
